import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MailIcon,
  LockIcon,
  LogInIcon,
  LoaderIcon,
  EyeIcon,
  EyeOffIcon,
  UserPlusIcon,
  AlertTriangleIcon,
  DownloadIcon
} from 'lucide-react';
import { thingsboardService } from '../services/thingsboard';
import { SignUpScreen } from './SignUpScreen';

interface LoginScreenProps {
  onLogin: (token: string) => void;
  initialSignUpMode?: boolean;
}

export function LoginScreen({ onLogin, initialSignUpMode }: LoginScreenProps) {
  const [isSignUpView, setIsSignUpView] = useState(() => {
    if (initialSignUpMode) return true;
    try {
      const searchParams = new URLSearchParams(window.location.search);
      return (
        searchParams.has('customer') ||
        searchParams.has('customerCode') ||
        searchParams.has('code') ||
        searchParams.has('customerId') ||
        searchParams.has('signup')
      );
    } catch {
      return false;
    }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingNotice, setPendingNotice] = useState('');

  // Clear any residual space selection data when arriving at login screen
  React.useEffect(() => {
    localStorage.removeItem('apartmentAssetId');
    localStorage.removeItem('apartmentLabel');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPendingNotice('');

    if (!email.trim()) {
      setError('이메일을 입력해주세요');
      return;
    }
    if (!password.trim()) {
      setError('비밀번호를 입력해주세요');
      return;
    }

    setIsLoading(true);
    try {
      const token = await thingsboardService.login(email.trim(), password, true);
      if (token) {
        // 1. Get current logged in user details
        const currentUser = await thingsboardService.getUser();
        const userId = currentUser?.id?.id;

        // 2. Tenant Admin or Customer Admin default pass
        if (currentUser?.authority === 'TENANT_ADMIN' || currentUser?.authority === 'SYS_ADMIN') {
          onLogin(token);
          return;
        }

        // 3. Check User additionalInfo & Server-Scope Attributes for approval status
        const additionalInfo = currentUser?.additionalInfo || {};
        let status = additionalInfo.status;

        // Fallback to server attributes check if not in additionalInfo
        if (!status && userId) {
          try {
            const attrs = await thingsboardService.getUserServerAttributes(userId);
            status = attrs.status;
          } catch {
            // Ignore if regular user cannot read server scope directly
          }
        }

        console.log('Login User status check:', status, currentUser);

        if (status === 'PENDING_APPROVAL') {
          await thingsboardService.logout();
          setPendingNotice('회원가입 승인 대기 중인 계정입니다. 관리자가 사용 승인 및 공간 권한을 지정한 후 로그인하실 수 있습니다.');
          return;
        }

        if (status === 'REJECTED') {
          await thingsboardService.logout();
          setError(additionalInfo.rejectReason || '가입 승인이 거절된 계정입니다. 관리자에게 문의해주세요.');
          return;
        }

        onLogin(token);
      } else {
        setError('이메일 또는 비밀번호가 올바르지 않습니다');
      }
    } catch (err: any) {
      const rawMsg = err.response?.data?.message || err.message || '';
      if (rawMsg.includes('User account is not active') || rawMsg.includes('not active')) {
        setError('사용자 계정이 아직 활성화되지 않았습니다.');
      } else if (rawMsg.includes('Invalid username or password') || rawMsg.includes('Bad credentials')) {
        setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      } else {
        setError(rawMsg || '로그인에 실패했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSignUpView) {
    return <SignUpScreen onBackToLogin={() => setIsSignUpView(false)} />;
  }

  return (
    <div
      className="h-[100dvh] max-w-md mx-auto w-full flex flex-col items-center justify-center px-6 overflow-hidden relative font-sans select-none"
      style={{ background: 'var(--bg-space, #0a0f1a)', color: 'var(--mobile-text-primary, #f8fafc)' }}
    >
      {/* Background subtle grid */}
      <div
        className="fixed inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at center, transparent 0%, var(--bg-space, #0a0f1a) 100%)' }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <motion.img
            src="/remove_background_fr.png"
            alt="SED"
            className="w-32 h-auto mb-3 opacity-80"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 0.8, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          />
          <motion.p
            className="text-[1.1rem] font-bold tracking-widest text-cyan-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Smart Edge Devices
          </motion.p>
        </div>

        {/* Pending Approval Notice Banner */}
        <AnimatePresence>
          {pendingNotice && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4 mb-4 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs flex items-start gap-2.5 shadow-lg"
            >
              <AlertTriangleIcon className="w-5 h-5 flex-shrink-0 text-amber-400 mt-0.5" />
              <div className="space-y-1">
                <b className="font-semibold text-amber-200">승인 대기 중</b>
                <p className="leading-relaxed text-[0.75rem]">{pendingNotice}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Login Card */}
        <motion.form
          onSubmit={handleSubmit}
          className="p-6 rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl space-y-4 shadow-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 px-3.5 py-2.5 rounded-xl font-medium"
            >
              {error}
            </motion.div>
          )}

          {/* Email input */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-300">이메일 (아이디)</label>
            <div className="relative flex items-center">
              <MailIcon className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full h-11 pl-10 pr-3.5 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Password input */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-300">비밀번호</label>
            <div className="relative flex items-center">
              <LockIcon className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 pl-10 pr-10 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 text-slate-500 hover:text-slate-300 transition-colors p-1"
              >
                {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full h-11 mt-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] disabled:opacity-50 cursor-pointer"
          >
            {isLoading ? (
              <>
                <LoaderIcon className="w-4 h-4 animate-spin" />
                <span>인증 확인 중...</span>
              </>
            ) : (
              <>
                <LogInIcon className="w-4 h-4" />
                <span>로그인</span>
              </>
            )}
          </button>

          {/* Sign up Link Button */}
          <div className="pt-2 text-center border-t border-white/5">
            <button
              type="button"
              onClick={() => setIsSignUpView(true)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 font-medium transition-colors py-1 cursor-pointer"
            >
              <UserPlusIcon className="w-3.5 h-3.5" />
              <span>계정이 없으신가요? <b>회원가입 신청</b></span>
            </button>
          </div>
        </motion.form>

        {/* Android App Download Button */}
        <div className="mt-4 flex justify-center">
          <a
            href="/sed_b2b.apk"
            download="sed_b2b.apk"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500/10 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/20 transition-all border border-cyan-500/20 shadow-sm"
          >
            <DownloadIcon className="w-4 h-4" />
            <span>안드로이드용 앱 다운로드 (APK)</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
}
